import { apiEmpleo } from "../../util/ApiEmpleo";

export type TResetCodeChannel = "email" | "sms";

export type TResetCodeFailureReason =
  | "INVALID"
  | "INVALID_CHANNEL"
  | "CANDIDATE_WITHOUT_EMAIL"
  | "CANDIDATE_WITHOUT_PHONE"
  | "TOO_MANY_REQUESTS"
  | "RESET_CODE_NOT_SENT"
  | "UNKNOWN";

export type TRequestPasswordResetCodeResult =
  | {
      kind: "sent";
      eventId: number;
      channel: TResetCodeChannel;
      destination: string;
    }
  | {
      kind: "failed";
      reason: TResetCodeFailureReason;
      retryAfterMinutes: number | null;
    };

export const DEFAULT_RETRY_AFTER_MINUTES = 15;

const KNOWN_FAILURE_REASONS: TResetCodeFailureReason[] = [
  "INVALID",
  "INVALID_CHANNEL",
  "CANDIDATE_WITHOUT_EMAIL",
  "CANDIDATE_WITHOUT_PHONE",
  "TOO_MANY_REQUESTS",
  "RESET_CODE_NOT_SENT",
];

const HTTP_TOO_MANY_REQUESTS = 429;
const HTTP_BAD_GATEWAY = 502;

const readFailureReason = (
  httpStatus: number | undefined,
  bodyStatus: unknown
): TResetCodeFailureReason => {
  const knownReason = KNOWN_FAILURE_REASONS.find(
    (reason) => reason === bodyStatus
  );
  if (knownReason) {
    return knownReason;
  }
  if (httpStatus === HTTP_TOO_MANY_REQUESTS) {
    return "TOO_MANY_REQUESTS";
  }
  if (httpStatus === HTTP_BAD_GATEWAY) {
    return "RESET_CODE_NOT_SENT";
  }
  return "UNKNOWN";
};

// Retry-After comes in seconds; it may be missing (not CORS-exposed) or be an HTTP date.
const readRetryAfterMinutes = (headers: unknown): number => {
  const rawValue = (headers as Record<string, unknown> | undefined)?.[
    "retry-after"
  ];
  const seconds = Number(rawValue);
  if (
    rawValue === undefined ||
    rawValue === "" ||
    !isFinite(seconds) ||
    seconds <= 0
  ) {
    return DEFAULT_RETRY_AFTER_MINUTES;
  }
  return Math.ceil(seconds / 60);
};

const failed = (
  reason: TResetCodeFailureReason,
  retryAfterMinutes: number | null = null
): TRequestPasswordResetCodeResult => ({
  kind: "failed",
  reason,
  retryAfterMinutes,
});

const apiRequestPasswordResetCode = async (
  username: string,
  channel: TResetCodeChannel
): Promise<TRequestPasswordResetCodeResult> => {
  try {
    const body = await apiEmpleo.post("users/resetPassword", {
      username,
      channel,
    });
    const data = body?.data;
    if (body?.status === "SUCCESS" && data?.eventId && data?.destination) {
      return {
        kind: "sent",
        eventId: data.eventId,
        channel: data.channel,
        destination: data.destination,
      };
    }
    return failed("UNKNOWN");
  } catch (e: any) {
    const reason = readFailureReason(
      e?.response?.status,
      e?.response?.data?.status
    );
    return failed(
      reason,
      reason === "TOO_MANY_REQUESTS"
        ? readRetryAfterMinutes(e?.response?.headers)
        : null
    );
  }
};

export { apiRequestPasswordResetCode };
